import { useEffect, useState, type FormEvent } from 'react';
import { RFQ_SCHEMA_VERSION, validateRfq, type RfqPayload } from '@shared/rfq';
import { ContactLinks } from './ContactLinks';
import { fmt, useI18n } from '@/i18n';
import { isApiAvailable, submitRfq } from '@/lib/api';

interface Form {
  company: string;
  contactPerson: string;
  email: string;
  phone: string;
  interest: string;
  message: string;
  consent: boolean;
  website: string;
}

export function ContactSection() {
  const { d, locale } = useI18n();
  const t = d.contact;
  const [f, setF] = useState<Form>({ company: '', contactPerson: '', email: '', phone: '', interest: t.interestOptions[0], message: '', consent: false, website: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [state, setState] = useState<{ kind: 'idle' | 'busy' | 'sent' | 'offline' | 'error'; text?: string }>({ kind: 'idle' });

  useEffect(() => {
    const onInterest = (e: Event) => {
      const v = (e as CustomEvent<string>).detail;
      if (t.interestOptions.includes(v)) setF((x) => ({ ...x, interest: v }));
    };
    window.addEventListener('rdk:interest', onInterest);
    return () => window.removeEventListener('rdk:interest', onInterest);
  }, [t.interestOptions]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload: RfqPayload = {
      schemaVersion: RFQ_SCHEMA_VERSION,
      kind: 'general',
      locale,
      contact: { company: f.company, contactPerson: f.contactPerson, email: f.email, phone: f.phone },
      order: null,
      configuration: null,
      productInterest: f.interest,
      comments: f.message,
      consent: f.consent,
      website: f.website,
      clientCreatedAt: new Date().toISOString(),
    };
    const v = validateRfq(payload);
    if (!v.ok) {
      setErrors(v.fields);
      document.getElementById(`c-${Object.keys(v.fields)[0]}`)?.focus();
      return;
    }
    setErrors({});
    setState({ kind: 'busy' });
    if (!(await isApiAvailable())) {
      setState({ kind: 'offline', text: d.errors.offline });
      return;
    }
    const res = await submitRfq(v.value);
    if (res.ok) setState({ kind: 'sent', text: fmt(t.sent, { n: res.rfqNumber }) });
    else setState({ kind: 'error', text: res.error === 'network' ? d.errors.network : d.errors.server });
  };

  const err = (k: string) => (errors[k] ? d.validation[errors[k].replace('validation.', '') as keyof typeof d.validation] : null);
  const input = (k: keyof Form, label: string, type = 'text', ac?: string) => (
    <div className={`field${errors[k] ? ' has-error' : ''}`}>
      <label htmlFor={`c-${k}`}>{label}</label>
      <input id={`c-${k}`} type={type} autoComplete={ac} value={f[k] as string} aria-invalid={!!errors[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
      {err(k) && <span className="field__error">{err(k)}</span>}
    </div>
  );

  return (
    <section id="contact" className="section section--contact" aria-labelledby="contact-title">
      <div className="wrap split split--contact">
        <div>
          <p className="eyebrow">{t.eyebrow}</p>
          <h2 id="contact-title" className="section__title">
            {t.title}
          </h2>
          <p className="section__lead">{t.lead}</p>
          <div className="contact-card">
            <p className="muted small">{t.or}</p>
            <ContactLinks className="contact-links--card" />
          </div>
        </div>

        {state.kind === 'sent' ? (
          <div className="form form--done" role="status">
            <p className="done__badge mono">✓</p>
            <p className="section__lead">{state.text}</p>
          </div>
        ) : (
          <form className="form" onSubmit={submit} noValidate>
            <div className="form__grid">
              {input('company', d.form.company, 'text', 'organization')}
              {input('contactPerson', d.form.contactPerson, 'text', 'name')}
              {input('email', d.form.email, 'email', 'email')}
              {input('phone', d.form.phone, 'tel', 'tel')}
            </div>
            <div className="field">
              <label htmlFor="c-interest">{t.interest}</label>
              <select id="c-interest" value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })}>
                {t.interestOptions.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="c-message">{t.message}</label>
              <textarea id="c-message" rows={4} placeholder={t.messagePh} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
            </div>
            <div className="hp" aria-hidden="true">
              <label htmlFor="c-website">Website</label>
              <input id="c-website" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
            </div>
            <label className={`check${errors.consent ? ' has-error' : ''}`}>
              <input id="c-consent" type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} />
              <span>{d.form.consent}</span>
            </label>
            {err('consent') && <span className="field__error">{err('consent')}</span>}
            {(state.kind === 'offline' || state.kind === 'error') && (
              <p className={`notice ${state.kind === 'offline' ? 'notice--warn' : 'notice--error'}`} role="alert">
                {state.text}
              </p>
            )}
            <button type="submit" className="btn btn--primary btn--lg" disabled={state.kind === 'busy'}>
              {state.kind === 'busy' ? t.sending : t.send}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
